import { groupBy, map, sortBy } from 'lodash';
import { ReceiptItem } from '@baemingo/receipt';
import { CardDetails } from '~reducers';
import { GenerateTemplateProps, ReceiptType } from './types';

/**
 * Structured receipt model.
 *
 * The text template in utils.ts is bound to fixed-width thermal printers. Devices that can
 * print images (Adyen terminals) get this structured document instead and lay it out with
 * real typography. Both are built from exactly the same inputs, so every legally required
 * line that appears on the text receipt is present here too.
 */

export interface ReceiptLabelValue {
  label: string;
  value: string;
}

export interface ReceiptDocumentItem {
  quantity: number;
  title: string;
  /** Formatted unit price, only set when the line holds more than one unit. */
  unitPrice?: string;
  /** Formatted line total after quantity, before discount. */
  total: string;
  variations: string[];
  /** Formatted discount amount (positive number, rendered as a deduction). */
  discount?: string;
  discountTitle?: string;
}

export interface ReceiptVatTable {
  /** "VAT" or "IGIC" depending on region. */
  title: string;
  columns: [string, string, string, string];
  rows: [string, string, string, string][];
}

export interface ReceiptPayment {
  method: string;
  amount: string;
  tip?: string;
  /** "(including 20,00 tip)", already translated. */
  tipNote?: string;
}

export interface ReceiptCard {
  brand?: string;
  /** Rendered as "•••• 9999". */
  maskedPan?: string;
  entryMode?: string;
  /** AID / TVR / TSI and similar EMV data. */
  details: ReceiptLabelValue[];
}

export interface ReceiptDocument {
  type: ReceiptType;
  /** Main heading, e.g. "KVITTO". */
  title: string;
  /** Inverted badge above the title: "KOPIA", "ÅTERKÖP", Norway's "IKKE KVITTERING FOR KJØP". */
  badge?: string;
  orderNumber?: string;
  introLines: string[];
  merchant: {
    companyName?: string;
    venueName?: string;
    address?: string;
    postalCode?: string;
    city?: string;
    vatNumberLabel: string;
    orgNumber?: string;
  };
  /** Spain: the customer the receipt is issued to. */
  customer: string[];
  meta: ReceiptLabelValue[];
  items: ReceiptDocumentItem[];
  totals?: {
    label: string;
    total: string;
    currency: string;
    discountLabel: string;
    discount?: string;
  };
  tipSuggestions?: { title: string; rows: ReceiptLabelValue[] };
  vat?: ReceiptVatTable;
  tipRefunded?: ReceiptLabelValue;
  paymentsTitle: string;
  payments: ReceiptPayment[];
  tipTotal?: ReceiptLabelValue;
  totalIncTip?: ReceiptLabelValue;
  card?: ReceiptCard;
  legal: ReceiptLabelValue[];
  bottomLines: string[];
  /** Invoice receipts carry a form to fill in by hand. */
  invoiceForm?: ReceiptLabelValue[];
  staffLine?: string;
}

const LOCALES: Record<string, string> = {
  SE: 'sv-SE',
  NO: 'nb-NO',
  DK: 'da-DK',
  FI: 'fi-FI',
  ES: 'es-ES',
  DE: 'de-DE',
  GB: 'en-GB',
  UK: 'en-GB',
  TR: 'tr-TR',
};

const CARD_BRANDS: Record<string, string> = {
  mc: 'Mastercard',
  mastercard: 'Mastercard',
  visa: 'Visa',
  visadankort: 'Visa/Dankort',
  dankort: 'Dankort',
  amex: 'American Express',
  maestro: 'Maestro',
  diners: 'Diners Club',
  discover: 'Discover',
  jcb: 'JCB',
  cup: 'UnionPay',
  unionpay: 'UnionPay',
  bcmc: 'Bancontact',
  girocard: 'girocard',
  applepay: 'Apple Pay',
  googlepay: 'Google Pay',
};

/** Formats a decimal amount for the receipt's country, e.g. 10 -> "10,00" in Sweden. */
export const createMoneyFormatter = (country?: string) => {
  const locale = LOCALES[(country || '').toUpperCase()] || 'en-GB';
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (value: number | string | undefined | null): string => {
    const numeric = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;

    if (numeric === undefined || numeric === null || Number.isNaN(numeric)) {
      return '';
    }

    // Intl uses U+2212 for minus in some locales; keep the ASCII hyphen printers know.
    return formatter.format(numeric).replace('−', '-');
  };
};

/** "mc" -> "Mastercard"; unknown brands are capitalised. */
export const humanizeCardBrand = (brand?: string) => {
  if (!brand) {
    return undefined;
  }

  const key = brand.toLowerCase().replace(/[^a-z]/g, '');
  return CARD_BRANDS[key] || brand.charAt(0).toUpperCase() + brand.slice(1);
};

/** "CLESS_CHIP" -> "Contactless", "Contactless chip" stays readable. */
export const humanizeEntryMode = (mode?: string) => {
  if (!mode) {
    return undefined;
  }

  const upper = mode.toUpperCase();

  if (upper.includes('CLESS') || upper.includes('CONTACTLESS') || upper.includes('TAP')) {
    return 'Contactless';
  }
  if (upper.includes('MAG') || upper.includes('SWIPE')) {
    return 'Magstripe';
  }
  if (upper.includes('CHIP') || upper.includes('ICC')) {
    return 'Chip';
  }
  if (upper.includes('MANUAL') || upper.includes('KEYED')) {
    return 'Manual';
  }

  return mode;
};

/** Reduces any masked PAN format to "•••• 9999". */
export const humanizeMaskedPan = (pan?: string) => {
  if (!pan) {
    return undefined;
  }

  const digits = pan.replace(/\D/g, '');
  const last4 = digits.slice(-4);

  return last4.length === 4 ? `•••• ${last4}` : pan.trim();
};

/** Groups identical items (same product, unit price, discount and variations) into one line. */
const buildItems = (
  items: ReceiptItem[],
  formatMoney: (value: number) => string,
): ReceiptDocumentItem[] => {
  const unitItems = items.map((item) =>
    item.fraction
      ? {
          ...item,
          amount: Math.round(item.amount / item.fraction),
          discount: Math.round(item.discount / item.fraction),
        }
      : item,
  );

  const groups = groupBy(unitItems, (item) => {
    const variations = [...(item.variations ?? [])].sort();
    return `${item.entryId}|${item.amount}|${item.discount}|${item.discountTitle ||
      ''}|${variations}`;
  });

  return map(groups, (group) => {
    const first = group[0];
    let quantity = 0;
    let total = 0;
    let discount = 0;

    group.forEach(({ fraction = 1, discount: itemDiscount }) => {
      quantity += fraction;
      total += first.amount * fraction;
      discount += itemDiscount * fraction;
    });

    const showUnitPrice = quantity !== 1 && quantity !== -1;

    return {
      quantity,
      title: first.title,
      unitPrice: showUnitPrice ? formatMoney(first.amount / 100) : undefined,
      total: formatMoney(total / 100),
      variations: first.variations ?? [],
      discount: discount !== 0 ? formatMoney(Math.abs(discount) / 100) : undefined,
      discountTitle: first.discountTitle,
    };
  });
};

const buildVatRows = (
  items: ReceiptItem[],
  formatMoney: (value: number) => string,
): [string, string, string, string][] => {
  const groups = groupBy(items, (item) => item.percentageVAT);

  const rows = map(groups, (group) => {
    const total = group.reduce((sum, item) => sum + item.amount - item.discount, 0) / 100;
    const net =
      group.reduce((sum, item) => {
        const price = item.amount - item.discount;
        return sum + price / (1 + item.percentageVAT / 100);
      }, 0) / 100;

    return { rate: group[0].percentageVAT, total, net, vat: total - net };
  });

  return sortBy(rows, (row) => row.rate).map((row) => [
    `${row.rate}%`,
    formatMoney(row.total),
    formatMoney(row.net),
    formatMoney(row.vat),
  ]);
};

const buildCard = (cardDetails: CardDetails | undefined): ReceiptCard | undefined => {
  if (!cardDetails) {
    return undefined;
  }

  const hasValues = Object.entries(cardDetails).some(
    ([key, value]) => key !== '__typename' && Boolean(value),
  );

  if (!hasValues) {
    return undefined;
  }

  const details: ReceiptLabelValue[] = [];
  if (cardDetails.aid) {
    details.push({ label: 'AID', value: cardDetails.aid });
  }
  if (cardDetails.tvr) {
    details.push({ label: 'TVR', value: cardDetails.tvr });
  }
  if (cardDetails.tsi) {
    details.push({ label: 'TSI', value: cardDetails.tsi });
  }

  return {
    brand: humanizeCardBrand(cardDetails.cardType),
    maskedPan: humanizeMaskedPan(cardDetails.maskedPan),
    entryMode: humanizeEntryMode(cardDetails.cardPaymentEntryMode),
    details,
  };
};

type BuildProps = Parameters<GenerateTemplateProps>[0];

/** Builds the structured receipt from the same inputs as generateTemplate(). */
export const buildReceiptDocument = ({
  type,
  payments,
  cardDetails,
  vars,
  isTable,
  receiptTemplate,
  isTypeInvoice = false,
  items = [],
  i18n,
  isCopy = false,
  country,
  showTipSuggestions,
  showStaffName,
  staff,
}: BuildProps): ReceiptDocument => {
  const formatMoney = createMoneyFormatter(country);
  const isNorway = country === 'NO';
  const isNorwayCheque = type === ReceiptType.Cheque && isNorway;
  const isRefund = type === ReceiptType.Refund;

  const receiptTypes: Record<ReceiptType, string> = {
    [ReceiptType.Copy]: i18n('Receipt.Types.Copy'),
    [ReceiptType.Refund]: i18n('Receipt.Types.Refund'),
    [ReceiptType.Sale]: i18n('Receipt.Types.Sale'),
    [ReceiptType.Cheque]: i18n('Receipt.Types.Cheque'),
    [ReceiptType.Invoice]: i18n('Receipt.Types.Invoice'),
    [ReceiptType.TipRefund]: i18n('Receipt.Types.TipRefund'),
  };

  // Title and badge. A copy of a sale is titled as a sale with a "COPY" badge; refunds and
  // pre-bills get their own badge so they can never be mistaken for a purchase receipt.
  let title = receiptTypes[type];
  let badge: string | undefined;

  if (isNorwayCheque) {
    title = 'Foreløpig kvittering';
    badge = 'IKKE KVITTERING FOR KJØP';
  } else if (type === ReceiptType.Copy) {
    title = i18n('Receipt.Types.Sale');
    badge = i18n('Receipt.Types.Copy');
  } else if (isCopy) {
    badge = i18n('Receipt.Types.Copy');
  } else if (type === ReceiptType.Refund || type === ReceiptType.TipRefund) {
    title = i18n('Receipt.Types.Sale');
    badge = receiptTypes[type];
  } else if (type === ReceiptType.Cheque) {
    badge = receiptTypes[type];
    title = i18n('Receipt.Types.Sale');
  }

  // Merchant block
  const merchant: ReceiptDocument['merchant'] = {
    companyName: vars.companyName !== vars.venueName ? vars.companyName : undefined,
    venueName: vars.venueName,
    address: vars.address,
    postalCode: vars.postalCode,
    city: vars.city,
    vatNumberLabel: i18n('Receipt.VATNo'),
    orgNumber: vars.orgNumber,
  };

  // Spain customer block
  const customer =
    country === 'ES' && vars.userName
      ? [
          vars.userName,
          vars.userOrgNumber,
          vars.userAddress,
          `${vars.userZip || ''} ${vars.userCity || ''}`.trim(),
          vars.userPhone,
        ].filter((line): line is string => Boolean(line))
      : [];

  // Meta rows
  const receiptNumberLabel =
    country === 'ES' && vars.userName
      ? i18n('Receipt.ReceiptNo.UserConnection')
      : i18n('Receipt.ReceiptNo');

  const meta: ReceiptLabelValue[] = [];
  if (vars.checkId) {
    meta.push({ label: receiptNumberLabel, value: vars.checkId });
  }
  if (vars.refundNumber) {
    meta.push({ label: i18n('Receipt.RefundNumber'), value: vars.refundNumber });
  }
  if (vars.datetime) {
    meta.push({ label: i18n('Receipt.Date'), value: vars.datetime });
  }
  if (vars.refundReference) {
    meta.push({ label: i18n('Receipt.RefundReference'), value: vars.refundReference });
  }
  if (vars.orderDate) {
    meta.push({ label: i18n('Receipt.OrderDate'), value: vars.orderDate });
  }
  if (vars.easyId) {
    meta.push({ label: 'ID', value: vars.easyId });
  }
  if (isTable) {
    meta.push({ label: i18n('Receipt.Table'), value: vars.tableCode || '' });
    if (vars.covers) {
      meta.push({ label: i18n('Receipt.Coverage'), value: vars.covers });
    }
  }

  // Items and totals
  const documentItems = buildItems(items, formatMoney);
  const itemsAmount = items.reduce((sum, item) => sum + item.amount - item.discount, 0);
  const hasDiscount = !!vars.totalDiscount && parseFloat(vars.totalDiscount) > 0;

  const totals: ReceiptDocument['totals'] | undefined = items.length
    ? {
        label: i18n('Receipt.Total'),
        total: formatMoney(itemsAmount / 100),
        currency: vars.currency || '',
        discountLabel: i18n('Receipt.Discount'),
        discount: hasDiscount ? formatMoney(vars.totalDiscount) : undefined,
      }
    : undefined;

  const tipSuggestions =
    items.length && type === ReceiptType.Cheque && showTipSuggestions
      ? {
          title: `${i18n('TM.Tip')} / ${i18n('Receipt.NewTotal')}`,
          rows: [5, 10, 15, 20].map((percent) => ({
            label: `${percent}%`,
            value: `${formatMoney((itemsAmount * (1 + percent / 100)) / 100)} ${vars.currency ||
              ''}`,
          })),
        }
      : undefined;

  const useIgic = items.some((item) => item.percentageVAT === 7) && country === 'ES';
  const vatTitle = useIgic ? 'IGIC' : i18n('Receipt.VAT');

  const vat: ReceiptVatTable | undefined =
    items.length && type !== ReceiptType.Cheque
      ? {
          title: vatTitle,
          columns: [`${vatTitle} %`, i18n('Receipt.Total'), i18n('Receipt.Net'), vatTitle],
          rows: buildVatRows(items, formatMoney),
        }
      : undefined;

  const tipRefunded =
    type === ReceiptType.TipRefund
      ? { label: i18n('Receipt.TipRefunded'), value: formatMoney(vars.tipRefunded) }
      : undefined;

  // Payments
  const documentPayments: ReceiptPayment[] = (payments ?? []).map((payment) => {
    const tip = payment.tip ? formatMoney(payment.tip) : undefined;

    return {
      method: ['ADYEN', 'ZETTLE', 'IZETTLE', 'LOOMIS'].includes(payment.type)
        ? i18n('Receipt.Card')
        : payment.type,
      amount: formatMoney(payment.amount),
      tip,
      tipNote: tip ? `(${i18n('Receipt.Including')} ${tip} ${i18n('Receipt.tip')})` : undefined,
    };
  });

  const hasTip = !!vars.tip && parseFloat(vars.tip) > 0;
  const sign = isRefund ? -1 : 1;

  const tipTotal = hasTip
    ? { label: i18n('Receipt.TotalTip'), value: formatMoney((sign * Number(vars.tip)) / 100) }
    : undefined;

  const totalIncTip =
    hasTip && vars.paymentIncTip
      ? {
          label: i18n('Receipt.TotalIncTip'),
          value: formatMoney((sign * Number(vars.paymentIncTip)) / 100),
        }
      : undefined;

  // Card: prefer live terminal details, fall back to what the backend stored.
  let card = payments ? buildCard(cardDetails) : undefined;

  if (payments && !card && (vars.cardIdentifier || vars.cardMethod)) {
    card = {
      brand: humanizeCardBrand(vars.cardMethod),
      maskedPan: humanizeMaskedPan(vars.cardIdentifier),
      entryMode: undefined,
      details: [],
    };
  }

  // Legal / device lines
  const legal: ReceiptLabelValue[] = [];
  if (vars.deviceName) {
    legal.push({ label: 'Reg', value: vars.deviceName });
  }
  if (vars.deviceId) {
    legal.push({ label: i18n('Receipt.ControlUnit'), value: vars.deviceId });
  }

  const invoiceForm: ReceiptLabelValue[] | undefined = isTypeInvoice
    ? [
        { label: `${i18n('Receipt.Types.Invoice')}`, value: '' },
        { label: `${i18n('General.Company')}`, value: '' },
        { label: `${i18n('Receipt.VATNo')}`, value: '' },
        { label: 'ATT', value: '' },
        { label: `${i18n('Receipt.InvoiceAddress')}`, value: '' },
        { label: `${i18n('Receipt.InvoiceAmount')}`, value: formatMoney(itemsAmount / 100) },
        { label: `${i18n('Receipt.Tip')}`, value: '' },
        { label: `${i18n('Receipt.TotalAmount')}`, value: '' },
        { label: `${i18n('Receipt.Signature')}`, value: '' },
        { label: `${i18n('Receipt.NameClarification')}`, value: '' },
        { label: `${i18n('Receipt.IDCheck')}`, value: '' },
      ]
    : undefined;

  const staffName = type === ReceiptType.Sale ? staff?.name : vars.staffOnOrder;
  const staffLine =
    showStaffName && staffName && [ReceiptType.Sale, ReceiptType.Copy].includes(type)
      ? i18n('Receipt.Staff', staffName)
      : undefined;

  return {
    type,
    title,
    badge,
    orderNumber: vars.orderNumber ? `#${vars.orderNumber}` : undefined,
    introLines: (receiptTemplate?.introLines ?? []).filter(Boolean),
    merchant,
    customer,
    meta,
    items: documentItems,
    totals,
    tipSuggestions,
    vat,
    tipRefunded,
    paymentsTitle: i18n('Receipt.Payment'),
    payments: documentPayments,
    tipTotal,
    totalIncTip,
    card,
    legal,
    bottomLines: (receiptTemplate?.bottomLines ?? []).filter(Boolean),
    invoiceForm,
    staffLine,
  };
};
